<template>
  <default-layout>
    <!-- ═══════════════════════════════════ HERO ═══════════════════════════════════ -->
    <div class="offering--header">
      <div class="container">
        <div class="offering-hero--copy">
          <div class="offering-hero--eyebrow">BeliRumah Agen</div>
          <h1 class="offering--title">
            {{ $t('offering.title') }}
          </h1>
          <div class="offering--desc">
            {{ $t('offering.desc') }}
          </div>
          <div class="offering-hero--actions">
            <button class="btn offering-hero--primary" @click="action">{{ actionText }}</button>
            <a
              class="offering-hero--link"
              :href="`${userWebsiteUrl}/syarat-ketentuan`"
              target="_blank"
            >
              Lihat ketentuan
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════ FEATURED ═══════════════════════════════════ -->
    <div class="offering--featured-shell">
      <div class="container">
        <featured-features-section />
      </div>
    </div>

    <!-- ═══════════════════════════════════ MAIN CONTENT ═══════════════════════════════════ -->
    <div class="offering--content">
      <div class="container">
        <!-- ── PACKAGES ── -->
        <div class="offering--packages">
          <div class="package-studio--header">
            <div>
              <span>Paket Membership</span>
              <h2>{{ $t('offering.choosePackage') }}</h2>
            </div>
            <p>Pilih paket dengan akses listing, koin, dan fitur unggulan yang sesuai kebutuhan.</p>
          </div>

          <package-cards :products="accountProducts" />

          <div class="price--disclaimer mt-2">
            *<a :href="`${userWebsiteUrl}/syarat-ketentuan`" target="_blank"
              >Syarat dan Ketentuan</a
            >
            Lainnya Berlaku
          </div>
        </div>

        <!-- ── BENEFITS — BENTO GRID ── -->
        <div class="offering--benefits">
          <div class="benefits--header">
            <h2 class="section--title">{{ $t('offering.whyTitle') }}</h2>
          </div>
          <div class="benefit-bento">
            <div class="bb-card bb-red">
              <div class="bb-shape"></div>
              <span class="bb-num">01</span>
              <h3>{{ $t('offering.benefit1') }}</h3>
            </div>

            <div class="bb-card bb-teal">
              <div class="bb-shape"></div>
              <span class="bb-num">02</span>
              <h3>{{ $t('offering.benefit2') }}</h3>
            </div>

            <div class="bb-card bb-amber">
              <div class="bb-shape-a"></div>
              <div class="bb-shape-b"></div>
              <span class="bb-num">03</span>
              <h3>{{ $t('offering.benefit3') }}</h3>
            </div>

            <div class="bb-card bb-purple">
              <div class="bb-shape"></div>
              <div class="bb-shape2"></div>
              <span class="bb-num">04</span>
              <h3>{{ $t('offering.benefit4') }}</h3>
            </div>

            <div class="bb-card bb-gray">
              <span class="bb-num">05</span>
              <div class="bb-divider"></div>
              <div class="bb-body">
                <h3>{{ $t('offering.benefit5') }}</h3>
              </div>
            </div>
          </div>
        </div>

        <!-- ── PROPERTY TYPES ── -->
        <div class="offering--property-types">
          <div class="section--header">
            <h2 class="section--eyebrow">{{ $t('offering.propertyAdsTitle') }}</h2>
            <div class="section--title">Semua Jenis Properti,<br />Satu Platform</div>
          </div>

          <div class="property-types--grid">
            <div
              class="property-type--card"
              v-for="(item, idx) in propertyAdsItems"
              :key="`property-ad-${idx}`"
              :class="{ active: activePropertyIdx === idx }"
              @click="selectProperty(idx)"
            >
              <div class="property-type--icon">
                <v-icon>{{ item.icon }}</v-icon>
              </div>
              <h3 class="property-type--name">{{ item.title }}</h3>
            </div>
          </div>

          <transition name="fade-slide">
            <div
              class="property-type--detail"
              v-if="activePropertyIdx !== null"
              :key="activePropertyIdx"
            >
              <div class="property-type--detail-icon">
                <v-icon>{{ propertyAdsItems[activePropertyIdx].icon }}</v-icon>
              </div>
              <div class="property-type--detail-body">
                <h3>{{ propertyAdsItems[activePropertyIdx].title }}</h3>
                <p>{{ propertyAdsItems[activePropertyIdx].desc }}</p>
              </div>
              <button class="property-type--close" @click.stop="activePropertyIdx = null">
                <v-icon>mdi-close</v-icon>
              </button>
            </div>
          </transition>
        </div>

        <!-- HOW TO - SEO VISIBLE STEPS -->
        <div class="offering--how-to">
          <div class="section--header how-to--header">
            <div>
              <h2 class="section--eyebrow">{{ $t('offering.howToListingTitle') }}</h2>
              <div class="section--title">6 langkah pasang iklan pertamamu</div>
            </div>
            <p class="section--sub">Iklan pertama bisa tayang dalam 10 menit.</p>
          </div>

          <div class="how-to--timeline">
            <article
              class="how-to--item"
              v-for="(item, idx) in howToListingItems"
              :key="`how-to-step-${idx}`"
            >
              <div class="how-to--number">{{ String(idx + 1).padStart(2, '0') }}</div>
              <div class="how-to--content">
                <div class="how-to--meta">
                  <v-icon>{{ item.icon }}</v-icon>
                  <span>{{ item.shortLabel }}</span>
                </div>
                <h3 class="how-to--title">{{ item.title }}</h3>
                <p class="how-to--desc">{{ item.desc }}</p>
              </div>
              <div
                v-if="idx < howToListingItems.length - 1"
                class="how-to--arrow"
                :class="{
                  'how-to--arrow-down': idx === 2,
                  'how-to--arrow-left': idx === 3 || idx === 4,
                }"
              >
                <v-icon>mdi-arrow-right</v-icon>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>

    <Media />

    <!-- ═══════════════════════════════════ FOOTER ═══════════════════════════════════ -->
    <div class="offering--footer">
      <div class="container">
        <div class="offering--footer-inner">
          <!-- FAQ accordion -->
          <div class="offering--faq">
            <h2 class="section--eyebrow">{{ $t('offering.faq') }}</h2>
            <div class="faq--title">Ada yang ingin ditanyakan?</div>
            <p class="faq--sub">Jawaban untuk pertanyaan yang paling sering kami terima.</p>

            <div class="faq--list">
              <div
                class="faq--item"
                v-for="(item, idx) in faqItems"
                :key="`faq-${idx}`"
                :class="{ open: openFaqIdx === idx }"
              >
                <button class="faq--question" @click="toggleFaq(idx)">
                  <h3>{{ item.question }}</h3>
                  <v-icon class="faq--chevron">mdi-chevron-down</v-icon>
                </button>
                <div class="faq--answer-wrap">
                  <div class="faq--answer">{{ item.answer }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- CTA card -->
          <div class="offering--cta-card">
            <div class="cta--badge">
              <v-icon>mdi-lightning-bolt</v-icon>
              Mulai Sekarang
            </div>

            <div class="cta--illus" aria-hidden="true">
              <div class="cta--illus-house"><v-icon>mdi-home-city</v-icon></div>
              <div class="cta--illus-arrow"><v-icon>mdi-arrow-right-thin</v-icon></div>
              <div class="cta--illus-coins"><span class="coin">Rp</span></div>
              <div class="cta--illus-arrow"><v-icon>mdi-arrow-right-thin</v-icon></div>
              <div class="cta--illus-chart"><v-icon>mdi-chart-line</v-icon></div>
            </div>

            <div class="cta--stats">
              <div class="cta--stat" v-for="stat in ctaStats" :key="stat.label">
                <span class="cta--stat-val">{{ stat.value }}</span>
                <span class="cta--stat-lbl">{{ stat.label }}</span>
              </div>
            </div>

            <h3 class="cta--title">Siap pasang iklan<br />properti pertamamu?</h3>
            <p class="cta--desc">
              Daftar gratis, pilih paket, dan mulai jangkau jutaan pembeli aktif di seluruh
              Indonesia.
            </p>

            <button class="cta--btn" @click="action">
              {{ actionText }}
              <v-icon>mdi-arrow-right</v-icon>
            </button>

            <div class="cta--note">
              *<a :href="`${userWebsiteUrl}/syarat-ketentuan`" target="_blank"
                >Syarat dan Ketentuan</a
              >
              Lainnya Berlaku
            </div>

            <div class="back--to-main-website">
              <a :href="userWebsiteUrl">{{ $t('offering.backToMainWebsite') }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </default-layout>
</template>

<script>
import DefaultLayout from '@/layouts/default';
import DefaultHeadMixin from '@/mixins/defaultHead';
import Media from '@/components/agent-offerings/media.vue';
import PackageCards from '@/components/agent-offerings/package-cards.vue';
import FeaturedFeaturesSection from '@/components/featured-features/featured-features-section.vue';

export default {
  mixins: [DefaultHeadMixin],
  async fetch({ store }) {
    const url = 'https://agen.belirumah.co/iklan-properti';
    const title = 'Pasang Iklan Properti Dijual | Jangkau Banyak Pembeli';
    const description =
      'Pasang iklan properti di BeliRumah. Jual rumah, apartemen, tanah, gudang, ruko, ruang usaha, gedung, kantor, pabrik atau villa lebih cepat dengan jangkauan pembeli yang luas. Daftar sebagai agen sekarang.';

    store.commit('head/SET_CONTENT', {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'og-url', property: 'og:url', content: url },
        { hid: 'og-type', property: 'og:type', content: 'website' },
        { hid: 'og-description', property: 'og:description', content: description },
      ],
      link: [{ rel: 'canonical', href: url, id: 'canonical' }],
    });
  },
  components: {
    Media,
    PackageCards,
    DefaultLayout,
    FeaturedFeaturesSection,
  },
  data() {
    return {
      activePropertyIdx: null,
      openFaqIdx: null,
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.global.loggedIn;
    },
    user() {
      return this.$store.state.global.user;
    },
    accountProducts() {
      return this.$store.state.product.accountProducts;
    },
    userWebsiteUrl() {
      return process.env.VUE_APP_USER_URL;
    },
    actionText() {
      let text = '';
      if (!this.loggedIn) {
        text = 'Daftar Sebagai Agen';
      } else if (this.user && !this.user.membership) {
        text = 'Beli Paket Membership';
      } else {
        text = 'Beli Paket Koin / Upgrade';
      }
      return text + '*';
    },
    benefitItems() {
      return [1, 2, 3, 4, 5].map((idx) => this.$t(`offering.benefit${idx}`));
    },
    propertyAdsItems() {
      const icons = [
        'mdi-home-city-outline',
        'mdi-office-building-outline',
        'mdi-map-outline',
        'mdi-storefront-outline',
        'mdi-warehouse',
        'mdi-domain',
        'mdi-briefcase-outline',
        'mdi-home-roof',
        'mdi-office-building',
        'mdi-factory',
      ];
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((idx) => ({
        title: this.$t(`offering.propertyAds${idx}Title`),
        desc: this.$t(`offering.propertyAds${idx}Desc`),
        icon: icons[idx - 1],
      }));
    },
    howToListingItems() {
      const icons = [
        'mdi-account-plus-outline',
        'mdi-package-variant-closed',
        'mdi-text-box-edit-outline',
        'mdi-image-multiple-outline',
        'mdi-bullhorn-outline',
        'mdi-chart-line',
      ];
      const shortLabels = [
        'Daftar',
        'Pilih Paket',
        'Isi Data',
        'Upload Foto',
        'Publish',
        'Monitor',
      ];
      const descs = [
        'Buat akun agen menggunakan email. Verifikasi identitasmu untuk kepercayaan calon pembeli.',
        'Pilih paket membership yang sesuai kebutuhan listing. Mulai dari Lite untuk coba-coba, hingga Executive untuk agen yang aktif scale up.',
        'Isi informasi properti selengkap mungkin: lokasi, harga, luas bangunan, luas tanah, fasilitas dan lainnya.',
        'Foto yang baik adalah penentu pertama calon pembeli. Iklan dengan 6+ foto mendapat 5× lebih banyak klik dibanding yang tanpa foto.',
        'Gunakan koin untuk menayangkan iklan. Pilih fitur repost agar propertimu muncul di posisi teratas hasil pencarian.',
        'Lacak tayangan, klik, dan pesan masuk dari satu dashboard.',
      ];
      const tips = [
        ['Gunakan email bisnis untuk kesan profesional', 'Verifikasi nomor HP untuk keamanan akun'],
        [
          'Bandingkan jumlah koin dan slot listing tiap paket',
          'Paket PRO direkomendasikan untuk agen aktif',
          'Masa aktif mulai dari 30 hingga 90 hari',
        ],
        [
          'Sertakan alamat lengkap dengan titik peta',
          'Isi spesifikasi detail properti seperti : LT, LB, kamar, garasi dll',
        ],
        [
          'Ambil foto di siang hari dengan cahaya alami',
          'Minimal 4 foto, idealnya 10 foto atau lebih',
        ],
        [
          'Iklan tayang dalam hitungan menit setelah submit',
          'Gunakan fitur repost di jam prime time (pagi & sore)',
          'Pantau performa iklan dari dashboard agen',
        ],
        [
          'Aktifkan notifikasi untuk respon lead yang cepat',
          'Analitik performa iklan tersedia secara real-time',
        ],
      ];
      return [1, 2, 3, 4, 5, 6].map((idx) => ({
        title: this.$t(`offering.howToListing${idx}`),
        shortLabel: shortLabels[idx - 1],
        icon: icons[idx - 1],
        desc: descs[idx - 1],
        tips: tips[idx - 1],
      }));
    },
    faqItems() {
      return [1, 2, 3, 4].map((idx) => ({
        question: this.$t(`offering.q${idx}`),
        answer: this.$t(`offering.a${idx}`),
      }));
    },
    ctaStats() {
      return [
        { value: '800+', label: 'Agen aktif' },
        { value: '<10 mnt', label: 'Waktu untuk memasang iklan' },
        { value: '25.000+', label: 'Listing aktif' },
      ];
    },
  },
  methods: {
    selectProperty(idx) {
      this.activePropertyIdx = this.activePropertyIdx === idx ? null : idx;
    },
    toggleFaq(idx) {
      this.openFaqIdx = this.openFaqIdx === idx ? null : idx;
    },
    action() {
      if (!this.loggedIn) {
        this.$router.push({ path: '/register' });
      } else if (this.user && !this.user.membership) {
        this.$router.push({ path: '/product' });
      } else {
        this.$router.push({ path: '/coin' });
      }
    },
    async checkTakeover() {
      let takeover = null;
      const cookieStr = document.cookie;
      const cookies = require('cookie').parse(cookieStr || '') || {};
      if (cookies.belirumaAgentTakeover) {
        takeover = cookies.belirumaAgentTakeover;
      }
      if (!takeover) {
        let detail = await this.$store.dispatch('takeover/getTakeover');
        if (detail) {
          this.$modal.show('modal--takeover');
        }
      }
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch('product/getMainPackages');
    } catch (e) {
      console.log('ERROR LOAD MAIN PACKAGES: ', e);
    }
    if (process.client) {
      let self = this;
      setTimeout(() => {
        self.checkTakeover();
      }, 1000);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/scss/utils/variables';

// ═══════════════════════════════════════════════════════════════
// TRANSITIONS
// ═══════════════════════════════════════════════════════════════
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(8px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

// ═══════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════
.offering--header {
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(215, 25, 32, 0.18);
  background: $color-beliruma-1;

  &::before,
  &::after {
    content: '';
    position: absolute;
    pointer-events: none;
  }

  &::before {
    inset: 0;
    background: radial-gradient(circle at 50% 18%, rgba(255, 255, 255, 0.22), transparent 28%),
      radial-gradient(circle at 72% 62%, rgba(255, 255, 255, 0.16), transparent 24%),
      radial-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px);
    background-size: auto, auto, 18px 18px;
    mask-image: linear-gradient(to bottom, transparent, #000 13%, #000 82%, transparent);
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 13%, #000 82%, transparent);
  }

  &::after {
    right: -260px;
    bottom: -300px;
    width: 720px;
    height: 720px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 50%;
    box-shadow: inset 0 0 0 72px rgba(255, 255, 255, 0.08);
  }

  .container {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    max-width: 1060px;
    padding-top: 118px;
    padding-bottom: 96px;
  }
}

.offering-hero--copy {
  position: relative;
  display: flex;
  max-width: 860px;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.offering-hero--eyebrow {
  display: inline-flex;
  margin-bottom: 22px;
  padding: 8px 11px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(14px);
  font-family: Poppins-Bold, sans-serif;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.offering--title {
  max-width: 920px;
  color: $color-white;
  text-align: center;
  font-family: Poppins-Bold, sans-serif;
  font-size: 64px;
  line-height: 1.1;
  letter-spacing: -0.05em;
  text-wrap: balance;
  margin-bottom: 22px;

  @media #{$phones} {
    font-size: 38px;
    line-height: 1.05;
    margin-bottom: 12px;
  }
}

.offering--desc {
  max-width: 660px;
  color: $color-white;
  text-align: center;
  font-family: Poppins, sans-serif;
  font-size: 16px;
  line-height: 1.8;
  text-wrap: pretty;

  @media #{$phones} {
    font-size: 14px;
    line-height: 1.7;
  }
}

.offering-hero--actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: center;
  margin-top: 34px;

  @media #{$phones} {
    width: 100%;
    gap: 10px;
    margin-top: 26px;
  }
}

.offering-hero--primary {
  min-height: 50px;
  padding: 0 28px;
  border: 1px solid rgba(255, 255, 255, 0.36);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  font-size: 14px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.26), 0 18px 36px rgba(106, 0, 0, 0.2);
  backdrop-filter: blur(16px);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.32), 0 22px 42px rgba(106, 0, 0, 0.24);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  @media #{$phones} {
    width: 100%;
    justify-content: center;
  }
}

.offering-hero--link {
  display: inline-flex;
  min-height: 50px;
  align-items: center;
  padding: 0 20px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: $color-beliruma-1;
  font-family: Poppins-Bold, sans-serif;
  font-size: 13px;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.55);
    color: $color-beliruma-1;
    text-decoration: none;
  }

  @media #{$phones} {
    width: 100%;
    justify-content: center;
  }
}

// ═══════════════════════════════════════════════════════════════
// FEATURED SHELL
// ═══════════════════════════════════════════════════════════════
.offering--featured-shell {
  border-bottom: 1px solid #edf0f4;
  background: #ffffff;
}

// ═══════════════════════════════════════════════════════════════
// MAIN CONTENT
// ═══════════════════════════════════════════════════════════════
.offering--content {
  padding-top: 64px;
  padding-bottom: 56px;
  background: #ffffff;

  @media #{$phones} {
    padding-top: 36px;
    padding-bottom: 32px;
  }
}

// ═══════════════════════════════════════════════════════════════
// SHARED SECTION HELPERS
// ═══════════════════════════════════════════════════════════════
.section--eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-family: Poppins-Bold, sans-serif;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $color-beliruma-1;
  margin-bottom: 8px;

  &::before {
    content: '';
    width: 14px;
    height: 2px;
    border-radius: 99px;
    background: $color-beliruma-1;
    display: block;
  }
}

.section--header {
  margin-bottom: 24px;
}

.section--title {
  font-family: Poppins-Bold, sans-serif;
  font-size: 26px;
  color: #17191d;
  line-height: 1.25;
  margin-bottom: 6px;

  @media #{$phones} {
    font-size: 20px;
  }
}

.section--sub {
  font-size: 13px;
  color: #6b7480;
  line-height: 1.7;
  max-width: 560px;
}

// ═══════════════════════════════════════════════════════════════
// PACKAGES
// ═══════════════════════════════════════════════════════════════
.offering--packages {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto 72px;
  padding: 30px;
  border: 1px solid #e3e7ee;
  border-radius: 28px;
  background: radial-gradient(rgba(32, 33, 36, 0.07) 1px, transparent 1px), #f7f8fa;
  background-size: 18px 18px;
  overflow: visible;

  @media #{$phones} {
    margin-bottom: 48px;
    padding: 20px;
    border-radius: 20px;
  }
}

.package-studio--header {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(260px, 0.65fr);
  gap: 24px;
  align-items: end;
  margin-bottom: 22px;

  span {
    color: $color-beliruma-1;
    font-family: Poppins-Bold, sans-serif;
    font-size: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  h2 {
    margin: 7px 0 0;
    color: #17191d;
    font-family: Poppins-Bold, sans-serif;
    font-size: 30px;
    line-height: 1.24;
  }

  p {
    margin: 0;
    color: #6b7480;
    font-family: Poppins, sans-serif;
    font-size: 14px;
    line-height: 1.7;
  }

  @media #{$phones} {
    grid-template-columns: 1fr;
    gap: 10px;

    h2 {
      font-size: 24px;
    }
  }
}

.offering--packages .price--disclaimer {
  margin: 14px 0 0;
  color: #6b7480;
  font-size: 12px;
}

// ═══════════════════════════════════════════════════════════════
// BENEFITS — BENTO GRID
// ═══════════════════════════════════════════════════════════════
.offering--benefits {
  max-width: 1200px;
  margin: 0 auto 72px;

  @media #{$phones} {
    margin-bottom: 40px;
  }
}

.benefits--header {
  margin-bottom: 20px;
}

.benefit-bento {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media #{$tablets} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media #{$phones} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.bb-card {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  padding: 20px;
  min-height: 148px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border: 1px solid transparent;

  .bb-num {
    position: absolute;
    top: 14px;
    right: 16px;
    font-family: Poppins-Bold, sans-serif;
    font-size: 44px;
    line-height: 1;
    letter-spacing: -0.04em;
    opacity: 0.18;
  }

  h3 {
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 16px;
    line-height: 1.45;
    margin: 0;
  }
}

// Card 1 — Red, spans 2 cols
.bb-red {
  grid-column: 1 / 3;
  background: $color-beliruma-1;
  border-color: rgba(255, 255, 255, 0.12);

  .bb-num {
    color: #ffffff;
  }

  .bb-label {
    color: rgba(255, 255, 255, 0.7);
  }

  h3 {
    color: #ffffff;
  }

  .bb-shape {
    position: absolute;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    border: 1.5px solid rgba(255, 255, 255, 0.18);
    top: -28px;
    left: 55%;
    pointer-events: none;
  }

  @media #{$phones} {
    grid-column: 1 / 3;
  }
}

// Card 2 — Teal, col 3
.bb-teal {
  background: #e1f5ee;
  border-color: #9fe1cb;

  .bb-num {
    color: #1d9e75;
  }

  .bb-label {
    color: #0f6e56;
  }

  h3 {
    color: #085041;
  }

  .bb-shape {
    position: absolute;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #9fe1cb;
    top: -12px;
    right: -12px;
    opacity: 0.5;
    pointer-events: none;
  }
}

// Card 3 — Amber, col 1
.bb-amber {
  background: #fdf6ee;
  border-color: #fac775;

  .bb-num {
    color: #ba7517;
  }

  .bb-label {
    color: #854f0b;
  }

  h3 {
    color: #633806;
  }

  .bb-shape-a {
    position: absolute;
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: #fac775;
    opacity: 0.4;
    top: 12px;
    right: 12px;
    transform: rotate(16deg);
    pointer-events: none;
  }

  .bb-shape-b {
    position: absolute;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #ef9f27;
    opacity: 0.25;
    top: 26px;
    right: 28px;
    transform: rotate(-8deg);
    pointer-events: none;
  }
}

// Card 4 — Purple, col 2-4
.bb-purple {
  grid-column: 2 / 4;
  background: #eeedfe;
  border-color: #afa9ec;

  .bb-num {
    color: #7f77dd;
  }

  .bb-label {
    color: #534ab7;
  }

  h3 {
    color: #3c3489;
  }

  .bb-shape {
    position: absolute;
    top: 10px;
    right: 20px;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 2px solid #afa9ec;
    opacity: 0.5;
    pointer-events: none;
  }

  .bb-shape2 {
    position: absolute;
    top: 24px;
    right: 34px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid #7f77dd;
    opacity: 0.3;
    pointer-events: none;
  }

  @media #{$phones} {
    grid-column: 1 / 3;
  }
}

// Card 5 — Gray, full width
.bb-gray {
  grid-column: 1 / 4;
  background: #f8f7f5;
  border-color: #d3d1c7;
  flex-direction: row;
  align-items: center;
  gap: 18px;
  min-height: 82px;
  padding: 18px 24px;

  .bb-num {
    position: static;
    font-size: 36px;
    color: #888780;
    opacity: 1;
    flex-shrink: 0;
    line-height: 1;
  }

  .bb-divider {
    width: 1px;
    height: 36px;
    background: #d3d1c7;
    flex-shrink: 0;
  }

  .bb-body {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .bb-label {
    color: #5f5e5a;
    margin: 0;
    opacity: 1;
  }

  h3 {
    color: #444441;
    font-size: 16px;
  }

  @media #{$tablets} {
    grid-column: 1 / 3;
  }

  @media #{$phones} {
    grid-column: 1 / 3;
    flex-direction: column;
    align-items: flex-start;

    .bb-divider {
      display: none;
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// PROPERTY TYPES
// ═══════════════════════════════════════════════════════════════
.offering--property-types {
  max-width: 1200px;
  margin: 0 auto 72px;
  background: #ffffff;
  border: 1px solid #eceff3;
  border-radius: 22px;
  padding: 32px;

  @media #{$phones} {
    padding: 20px;
    border-radius: 18px;
    margin-bottom: 40px;
  }
}

.property-types--grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;

  @media #{$tablets} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media #{$phones} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.property-type--card {
  position: relative;
  overflow: hidden;
  padding: 14px 12px 12px;
  border: 1px solid #eceff3;
  border-radius: 14px;
  background: #fbfcfd;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  transition: border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 0% 0%, rgba(215, 25, 32, 0.06), transparent 60%);
    opacity: 0;
    transition: opacity 0.22s ease;
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(215, 25, 32, 0.3);
    background: #ffffff;
    transform: translateY(-2px);

    &::before {
      opacity: 1;
    }

    .property-type--icon {
      background: rgba(215, 25, 32, 0.14);
    }
  }

  &.active {
    border-color: $color-beliruma-1;
    background: rgba(215, 25, 32, 0.03);

    &::before {
      opacity: 1;
    }

    .property-type--icon {
      background: rgba(215, 25, 32, 0.14);
    }
  }
}

.property-type--icon {
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(215, 25, 32, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  .v-icon {
    color: $color-beliruma-1;
    font-size: 18px;
  }
}

.property-type--name {
  position: relative;
  z-index: 1;
  font-size: 12px;
  font-family: Poppins-SemiBold, sans-serif;
  color: #202124;
  line-height: 1.3;
}

.property-type--detail {
  margin-top: 14px;
  padding: 16px 18px;
  border: 1px solid rgba(215, 25, 32, 0.25);
  border-radius: 14px;
  background: rgba(215, 25, 32, 0.03);
  display: flex;
  align-items: flex-start;
  gap: 14px;
  position: relative;

  .property-type--detail-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(215, 25, 32, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;

    .v-icon {
      color: $color-beliruma-1;
      font-size: 22px;
    }
  }

  .property-type--detail-body {
    flex: 1;
    min-width: 0;

    h3 {
      font-family: Poppins-SemiBold, sans-serif;
      font-size: 14px;
      color: #17191d;
      margin-bottom: 4px;
    }

    p {
      font-size: 13px;
      color: #6b7480;
      line-height: 1.65;
      margin: 0;
    }
  }
}

.property-type--close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(32, 33, 36, 0.06);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(32, 33, 36, 0.12);
  }

  .v-icon {
    font-size: 14px;
    color: #6b7480;
  }
}

// HOW TO
.offering--how-to {
  max-width: 1200px;
  margin: 0 auto 16px;
  background: #ffffff;
  border: 1px solid #eceff3;
  border-radius: 22px;
  padding: 34px;
  box-shadow: 0 18px 48px rgba(19, 24, 31, 0.06);

  @media #{$phones} {
    padding: 22px 18px;
    border-radius: 18px;
  }
}

.how-to--header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 28px;

  @media #{$phones} {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 22px;
  }
}

.how-to--header .section--sub {
  max-width: 330px;
  margin-bottom: 2px;
  text-align: right;

  @media #{$phones} {
    max-width: none;
    text-align: left;
  }
}

.how-to--timeline {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px 24px;

  @media #{$tablets} {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  @media #{$phones} {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}

.how-to--item {
  position: relative;
  display: grid;
  grid-template-columns: 46px 1fr;
  gap: 14px;
  min-height: 250px;
  padding: 18px;
  border: 1px solid #eef0f3;
  border-radius: 18px;
  background: linear-gradient(145deg, #ffffff 0%, #fbfcfd 100%);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &::before {
    content: '';
    position: absolute;
    left: 41px;
    top: 68px;
    bottom: 22px;
    width: 1px;
    background: linear-gradient(180deg, rgba(215, 25, 32, 0.3), rgba(215, 25, 32, 0));
  }

  &:hover {
    border-color: rgba(215, 25, 32, 0.22);
    box-shadow: 0 16px 34px rgba(19, 24, 31, 0.07);
    transform: translateY(-2px);
  }

  &:first-child {
    background: linear-gradient(145deg, rgba(215, 25, 32, 0.055) 0%, #ffffff 62%);
  }

  &:last-child {
    border-color: rgba(215, 25, 32, 0.2);
  }

  @media (min-width: 1366px) {
    &:nth-child(4) {
      grid-column: 3;
      grid-row: 2;
    }

    &:nth-child(5) {
      grid-column: 2;
      grid-row: 2;
    }

    &:nth-child(6) {
      grid-column: 1;
      grid-row: 2;
    }
  }

  @media #{$phones} {
    grid-template-columns: 44px 1fr;
    gap: 12px;
    min-height: 0;
    padding: 17px;

    &::before {
      left: 38px;
      top: 64px;
      bottom: 18px;
    }
  }
}

.how-to--number {
  position: relative;
  z-index: 1;
  width: 46px;
  height: 46px;
  border-radius: 16px;
  background: #17191d;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-family: Poppins-Bold, sans-serif;
  box-shadow: inset 0 -10px 20px rgba(255, 255, 255, 0.08);

  @media #{$phones} {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    font-size: 13px;
  }
}

.how-to--content {
  min-width: 0;
}

.how-to--meta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 26px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(215, 25, 32, 0.07);
  color: $color-beliruma-1;
  font-size: 10px;
  font-family: Poppins-Bold, sans-serif;
  margin-bottom: 9px;

  .v-icon {
    font-size: 14px;
    color: $color-beliruma-1;
  }
}

.how-to--title {
  font-family: Poppins-Bold, sans-serif;
  font-size: 15.5px;
  color: #17191d;
  line-height: 1.35;
  margin-bottom: 8px;

  @media #{$phones} {
    font-size: 15px;
  }
}

.how-to--desc {
  font-size: 12px;
  color: #5f6875;
  line-height: 1.7;
  margin-bottom: 12px;
}

.how-to--tips {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 7px;

  li {
    position: relative;
    padding-left: 13px;
    font-size: 11px;
    color: #4d5662;
    line-height: 1.55;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 7px;
      width: 5px;
      height: 5px;
      border-radius: 99px;
      background: $color-beliruma-1;
    }
  }
}

.how-to--arrow {
  position: absolute;
  top: 50%;
  right: -15px;
  z-index: 2;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid rgba(215, 25, 32, 0.18);
  box-shadow: 0 10px 22px rgba(19, 24, 31, 0.08);
  transform: translateY(-50%);

  .v-icon {
    font-size: 18px;
    color: $color-beliruma-1;
  }

  &.how-to--arrow-down {
    top: auto;
    right: 50%;
    bottom: -16px;
    transform: translateX(50%) rotate(90deg);
  }

  &.how-to--arrow-left {
    right: auto;
    left: -15px;
    transform: translateY(-50%) rotate(180deg);
  }

  @media #{$tablets} {
    top: auto;
    right: 50%;
    bottom: -15px;
    left: auto;
    width: 28px;
    height: 28px;
    transform: translateX(50%) rotate(90deg);

    &.how-to--arrow-down,
    &.how-to--arrow-left {
      top: auto;
      right: 50%;
      bottom: -15px;
      left: auto;
      transform: translateX(50%) rotate(90deg);
    }
  }

  @media #{$phones} {
    top: auto;
    right: 50%;
    bottom: -15px;
    left: auto;
    width: 28px;
    height: 28px;
    transform: translateX(50%) rotate(90deg);

    &.how-to--arrow-down,
    &.how-to--arrow-left {
      top: auto;
      right: 50%;
      bottom: -15px;
      left: auto;
      transform: translateX(50%) rotate(90deg);
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════════════
.offering--footer {
  background: #f8fafc;

  .container {
    max-width: 1100px;
    padding-top: 72px;
    padding-bottom: 64px;

    @media #{$phones} {
      padding: 48px 22px;
    }
  }
}

.offering--footer-inner {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 48px;
  align-items: start;

  @media #{$tablets} {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  @media #{$phones} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

// FAQ
.offering--faq {
  .section--eyebrow {
    margin-bottom: 8px;
  }
}

.faq--title {
  font-family: Poppins-Bold, sans-serif;
  font-size: 26px;
  color: #17191d;
  line-height: 1.25;
  margin-bottom: 4px;

  @media #{$phones} {
    font-size: 20px;
  }
}

.faq--sub {
  font-size: 13px;
  color: #6b7480;
  line-height: 1.65;
  margin-bottom: 24px;
}

.faq--list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.faq--item {
  border: 1px solid #eceff3;
  border-radius: 14px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &.open {
    border-color: rgba(215, 25, 32, 0.28);
  }
}

.faq--question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 18px;
  background: none;
  border: none;
  cursor: pointer;
  gap: 14px;
  text-align: left;

  span {
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 14px;
    color: #17191d;
    line-height: 1.4;
  }

  .faq--chevron {
    flex-shrink: 0;
    color: #8d96a5;
    font-size: 20px;
    transition: transform 0.25s ease, color 0.2s ease;
  }
}

.faq--item.open .faq--question .faq--chevron {
  transform: rotate(180deg);
  color: $color-beliruma-1;
}

.faq--answer-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease;
}

.faq--item.open .faq--answer-wrap {
  grid-template-rows: 1fr;
}

.faq--answer {
  overflow: hidden;
  font-size: 13px;
  color: #6b7480;
  line-height: 1.7;
  padding: 0 18px;
  transition: padding 0.3s ease;
}

.faq--item.open .faq--answer {
  padding: 0 18px 16px;
}

// CTA Card
.offering--cta-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #eceff3;
  border-radius: 22px;
  background: #ffffff;
  padding: 28px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  &::before {
    content: '';
    position: absolute;
    top: -60px;
    right: -60px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(215, 25, 32, 0.08), transparent 70%);
    pointer-events: none;
  }

  @media #{$tablets} {
    display: none; // Hide on tablet as per original logic
  }

  @media #{$phones} {
    display: flex; // Show on mobile
    padding: 24px;
    border-radius: 18px;
  }
}

.cta--badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 99px;
  background: rgba(215, 25, 32, 0.08);
  font-size: 11px;
  font-family: Poppins-Bold, sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $color-beliruma-1;

  .v-icon {
    font-size: 14px;
    color: $color-beliruma-1;
  }
}

.cta--illus {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  background: rgba(215, 25, 32, 0.03);
  border-radius: 14px;
  border: 1px solid rgba(215, 25, 32, 0.1);
}

.cta--illus-house,
.cta--illus-chart {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(215, 25, 32, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  .v-icon {
    font-size: 24px;
    color: $color-beliruma-1;
  }
}

.cta--illus-arrow {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  .v-icon {
    font-size: 20px;
    color: rgba(215, 25, 32, 0.28);
  }
}

.cta--illus-coins {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(215, 25, 32, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  .coin {
    font-family: Poppins-Bold, sans-serif;
    font-size: 15px;
    color: $color-beliruma-1;
  }
}

.cta--stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  width: 100%;
  padding: 14px 0;
  border-top: 1px solid #eceff3;
  border-bottom: 1px solid #eceff3;
}

.cta--stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cta--stat-val {
  font-family: Poppins-Bold, sans-serif;
  font-size: 20px;
  color: #17191d;
  line-height: 1;
}

.cta--stat-lbl {
  font-size: 11px;
  color: #8d96a5;
  line-height: 1.4;
}

.cta--title {
  font-family: Poppins-Bold, sans-serif;
  font-size: 22px;
  color: #17191d;
  line-height: 1.3;
  margin: 0;

  @media #{$phones} {
    font-size: 20px;
  }
}

.cta--desc {
  font-size: 13px;
  color: #6b7480;
  line-height: 1.65;
  margin: 0;
}

.cta--btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 24px;
  border-radius: 99px;
  background: $color-beliruma-1;
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;
  text-decoration: none;
  width: 100%;
  justify-content: center;

  .v-icon {
    font-size: 16px;
    color: #ffffff;
  }

  &:hover {
    transform: translateY(-2px);
    background: darken(#d71920, 5%);
  }

  &:active {
    transform: scale(0.97);
  }
}

.cta--note {
  font-size: 11px;
  color: #8d96a5;
  margin: 0;
  text-align: center;
  width: 100%;

  a {
    color: $color-beliruma-1;
    text-decoration: none;
  }
}

.back--to-main-website {
  width: 100%;
  text-align: center;

  a {
    font-size: 13px;
    color: $color-beliruma-1;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// RESPONSIVE — TABLETS
// ═══════════════════════════════════════════════════════════════
@media #{$tablets} {
  .offering--header .container {
    max-width: 940px;
    padding-top: 76px;
    padding-bottom: 64px;
  }

  .offering--title {
    font-size: 44px;
    letter-spacing: -0.045em;
  }

  .offering--desc {
    max-width: 610px;
    font-size: 15px;
    line-height: 1.65;
  }

  .offering-hero--actions {
    margin-top: 28px;
  }

  .offering--content {
    padding-top: 48px;
    padding-bottom: 44px;
  }

  .section--title {
    font-size: 23px;
  }

  .section--sub {
    font-size: 12.5px;
  }

  .offering--packages {
    margin-bottom: 54px;
    padding: 22px;
    border-radius: 22px;
  }

  .package-studio--header {
    grid-template-columns: minmax(0, 1fr) minmax(220px, 0.72fr);
    gap: 18px;
    margin-bottom: 18px;

    span {
      font-size: 11px;
    }

    h2 {
      font-size: 25px;
    }

    p {
      font-size: 13px;
      line-height: 1.6;
    }
  }

  .offering--benefits,
  .offering--property-types {
    margin-bottom: 54px;
  }

  .bb-card {
    min-height: 128px;
    padding: 17px;

    .bb-num {
      font-size: 38px;
    }

    h3 {
      font-size: 14.5px;
    }
  }

  .bb-gray {
    min-height: 74px;
    padding: 16px 20px;
  }

  .offering--property-types {
    padding: 24px;
    border-radius: 20px;
  }

  .property-type--card {
    padding: 12px 10px 11px;
  }

  .property-type--icon {
    width: 32px;
    height: 32px;

    .v-icon {
      font-size: 16px;
    }
  }

  .property-type--name {
    font-size: 11.5px;
  }

  .offering--how-to {
    padding: 26px;
    border-radius: 20px;
  }

  .how-to--header {
    gap: 18px;
    margin-bottom: 22px;
  }

  .how-to--timeline {
    gap: 16px;
  }

  .how-to--item {
    grid-template-columns: 42px 1fr;
    gap: 12px;
    min-height: 0;
    padding: 15px;
    border-radius: 16px;

    &::before {
      left: 36px;
      top: 62px;
      bottom: 18px;
    }
  }

  .how-to--number {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    font-size: 13px;
  }

  .how-to--title {
    font-size: 14.5px;
  }

  .how-to--desc {
    margin-bottom: 9px;
    font-size: 11.5px;
    line-height: 1.55;
  }

  .how-to--tips li {
    font-size: 10.5px;
    line-height: 1.45;
  }

  .offering--cta-card {
    display: none;
  }
}

// ═══════════════════════════════════════════════════════════════
// RESPONSIVE — PHONES
// ═══════════════════════════════════════════════════════════════
@media #{$phones} {
  .offering--header .container {
    padding: 64px 22px 58px;
  }

  .offering-hero--eyebrow {
    margin-bottom: 16px;
    font-size: 10px;
  }

  .offering--title {
    font-size: 38px;
    line-height: 1.05;
  }

  .offering--desc {
    font-size: 14px;
    line-height: 1.7;
  }

  .offering-hero--actions {
    width: 100%;
    gap: 10px;
    margin-top: 26px;
  }

  .offering-hero--primary,
  .offering-hero--link {
    width: 100%;
    justify-content: center;
  }

  .offering--content {
    padding-top: 36px;
    padding-bottom: 32px;
  }

  .offering--packages {
    margin-bottom: 48px;
    padding: 20px;
    border-radius: 20px;
  }

  .package-studio--header {
    grid-template-columns: 1fr;
    gap: 10px;

    h2 {
      font-size: 24px;
    }
  }
}
</style>
